import React from 'react';
import { useReducedMotion } from 'framer-motion';
import styles from './subpage-hero.module.css';

/** Decorative example only; the real queue position is displayed below the hero. */
const WaitingCountdown: React.FC = () => {
    const ref = React.useRef<SVGGElement>(null);
    const reducedMotion = useReducedMotion();
    const [count, setCount] = React.useState(99);

    React.useEffect(() => {
        if (reducedMotion) {
            setCount(0);
            return undefined;
        }
        if (!ref.current) return undefined;
        let timer: ReturnType<typeof setInterval> | undefined;
        let tick = 0;
        const stop = () => {
            clearInterval(timer);
            timer = undefined;
        };
        const observer = new IntersectionObserver(([entry]) => {
            stop();
            if (!entry.isIntersecting) return;
            timer = setInterval(() => {
                tick = (tick + 1) % 125;
                setCount(Math.max(0, 99 - tick));
            }, 80);
        });
        observer.observe(ref.current);
        return () => {
            stop();
            observer.disconnect();
        };
    }, [reducedMotion]);

    return (
        <g ref={ref} aria-hidden="true">
            <rect x="270" y="212" width="103" height="44" rx="12" fill="#fdf7f2" stroke="#d9cdc5" />
            <text
                x="321.5"
                y="234"
                textAnchor="middle"
                dominantBaseline="central"
                fill="#179eff"
                fontSize="22"
                fontWeight="600"
                fontFamily="monospace"
                letterSpacing="3"
            >
                {count.toString().padStart(4, '0')}
            </text>
        </g>
    );
};

export type HeroVisual = 'pricing' | 'contact' | 'calculator' | 'help' | 'partner' | 'waiting';

const Check = ({ x, y }: { x: number; y: number }) => (
    <path
        className={styles.check}
        pathLength="1"
        d={`m${x} ${y} 5 5 10-12`}
        stroke="#179eff"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
    />
);

export const HeroMotif = ({ kind }: { kind: HeroVisual }) => {
    switch (kind) {
        case 'pricing':
            return (
                <g>
                    <rect x="105" y="95" width="218" height="174" rx="14" fill="#fdf7f2" stroke="#d9cdc5" />
                    <rect x="125" y="113" width="62" height="7" rx="3.5" fill="#a99c91" />
                    <text x="125" y="154" fill="#231d61" fontSize="27" fontWeight="600">
                        Premium
                    </text>
                    <path d="M125 174h178" stroke="#d9cdc5" />
                    {[197, 221, 245].map((y) => (
                        <g key={y}>
                            <Check x={126} y={y} />
                            <path d={`M155 ${y}h112`} stroke="#d9cdc5" strokeWidth="5" strokeLinecap="round" />
                        </g>
                    ))}
                    <g className={styles.confirmation}>
                        <circle cx="332" cy="130" r="30" fill="#fdf7f2" stroke="#179eff" />
                        <path
                            d="m315 129 9 9 23-24"
                            stroke="#179eff"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </g>
                </g>
            );
        case 'calculator':
            return (
                <g>
                    <path d="M87 221h265M87 179h265M87 137h265" stroke="#d9cdc5" strokeDasharray="3 6" />
                    <path
                        className={styles.chart}
                        pathLength="1"
                        d="M88 219C167 212 232 184 345 105"
                        stroke="#179eff"
                        strokeWidth="4"
                        strokeLinecap="round"
                    />
                    <g className={styles.confirmation}>
                        <rect x="270" y="163" width="112" height="114" rx="14" fill="#fdf7f2" stroke="#d9cdc5" />
                        <rect x="286" y="178" width="80" height="23" rx="5" fill="#eee6de" />
                        {[216, 239, 260].map((y) =>
                            [294, 326, 358].map((x) => (
                                <circle
                                    key={`${x}-${y}`}
                                    cx={x}
                                    cy={y}
                                    r="5"
                                    fill={x === 358 ? '#179eff' : '#d9cdc5'}
                                />
                            )),
                        )}
                        <path d="M323 187h34" stroke="#231d61" strokeWidth="3" strokeLinecap="round" />
                    </g>
                </g>
            );
        case 'help':
            return (
                <g>
                    <g className={styles.message}>
                        <path
                            d="M91 113a10 10 0 0 1 10-10h167a10 10 0 0 1 10 10v45a10 10 0 0 1-10 10H119l-28 17v-72Z"
                            fill="#eee6de"
                        />
                        <path d="M113 125h131m-131 18h90" stroke="#a99c91" strokeWidth="5" strokeLinecap="round" />
                    </g>
                    <g className={styles.reply}>
                        <path
                            d="M204 181a10 10 0 0 1 10-10h167a10 10 0 0 1 10 10v42l-23-13H214a10 10 0 0 1-10-10v-19Z"
                            fill="#fdf7f2"
                            fillOpacity="0.8"
                            stroke="#179eff"
                        />
                        <path d="M226 190h103" stroke="#231d61" strokeWidth="5" strokeLinecap="round" />
                    </g>
                </g>
            );
        case 'contact':
            return (
                <g>
                    <g className={styles.message}>
                        <rect x="113" y="101" width="188" height="112" rx="12" fill="#fdf7f2" stroke="#d9cdc5" />
                        <path d="m117 108 90 65 90-65M117 207" stroke="#d9cdc5" />
                        <path d="m117 207 58-45m122 45-58-45" stroke="#d9cdc5" />
                        <path d="m117 108 90 65 90-65" stroke="#179eff" strokeWidth="2.5" strokeLinejoin="round" />
                    </g>
                    <g className={styles.reply}>
                        <path
                            d="M259 194h101a12 12 0 0 1 12 12v28a12 12 0 0 1-12 12h-70l-24 17v-17h-7a12 12 0 0 1-12-12v-28a12 12 0 0 1 12-12Z"
                            fill="#fdf7f2"
                            stroke="#d9cdc5"
                        />
                        <circle cx="287" cy="220" r="4" fill="#179eff" />
                        <circle cx="310" cy="220" r="4" fill="#179eff" />
                        <circle cx="333" cy="220" r="4" fill="#179eff" />
                    </g>
                </g>
            );
        case 'partner':
            return (
                <g>
                    <path
                        className={styles.chart}
                        pathLength="1"
                        d="m138 137 80 73 82-73M218 210v37"
                        stroke="#179eff"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                    />
                    {[138, 300].map((x) => (
                        <g key={x}>
                            <circle cx={x} cy="126" r="35" fill="#fdf7f2" stroke="#d9cdc5" />
                            <circle cx={x} cy="119" r="10" stroke="#a99c91" strokeWidth="2" />
                            <path
                                d={`M${x - 18} 147c0-23 36-23 36 0`}
                                stroke="#a99c91"
                                strokeWidth="2"
                                strokeLinecap="round"
                            />
                        </g>
                    ))}
                    <g className={styles.confirmation}>
                        <circle cx="218" cy="221" r="35" fill="#fdf7f2" stroke="#179eff" />
                        <path
                            d="m202 220 10 10 22-25"
                            stroke="#179eff"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </g>
                </g>
            );
        case 'waiting':
            return (
                <g>
                    <circle cx="209" cy="170" r="66" fill="#fdf7f2" stroke="#d9cdc5" />
                    <path
                        d="M209 113v8m57 49h-8m-49 57v-8m-57-49h8"
                        stroke="#a99c91"
                        strokeWidth="2"
                        strokeLinecap="round"
                    />
                    <path
                        className={styles.clockHand}
                        d="M209 170v-37"
                        stroke="#179eff"
                        strokeWidth="3"
                        strokeLinecap="round"
                    />
                    <path d="m209 170 26 15" stroke="#231d61" strokeWidth="3" strokeLinecap="round" />
                    <circle cx="209" cy="170" r="4" fill="#179eff" />
                    <WaitingCountdown />
                </g>
            );
        default:
            return null;
    }
};
